import React, { useState, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
// Input replaced with native inputs for OTP boxes to allow fixed widths
import Button from '../common/Button/index'
import Typography from '../common/Typography/index'
import Card from '../common/Card/index'
import { sendVerifyPhone, confirmVerifyPhone, resetPhoneVerification } from '../../state/redux/actions/userActions'

const AdminPhoneVerification = ({ onSuccess, onCancel, targetPhone = null, countryCode = null, isNewPhoneAdd = false }) => {
  const dispatch = useDispatch()
  const { phoneVerification } = useSelector(state => state.user)
  const [code, setCode] = useState(['', '', '', '', '', ''])
  const [timeLeft, setTimeLeft] = useState(0)
  const [isResending, setIsResending] = useState(false)
  const inputsRef = useRef([])

  // Countdown timer
  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000)
      return () => clearTimeout(timer)
    }
  }, [timeLeft])

  // Format time display
  const formatTime = seconds => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  // Auto-submit logic
  useEffect(() => {
    if (code.every(digit => digit !== '')) {
      const submitTimer = setTimeout(() => {
        handleVerification()
      }, 500)
      return () => clearTimeout(submitTimer)
    }
  }, [code])

  // Handle verification success
  useEffect(() => {
    if (phoneVerification.verified) {
      onSuccess?.()
    }
  }, [phoneVerification.verified, onSuccess])

  // Handle input change
  const handleChange = (index, value) => {
    // Only allow digits
    if (!/^\d*$/.test(value)) return

    const newCode = [...code]
    newCode[index] = value

    setCode(newCode)

    // Auto-focus next input
    if (value && index < 5) {
      inputsRef.current[index + 1]?.focus()
    }
  }

  // Handle key down events
  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      const inputValue = e.target.value

      if (inputValue === '') {
        e.preventDefault()
        if (index > 0) {
          const newCode = [...code]
          newCode[index - 1] = ''
          setCode(newCode)
          inputsRef.current[index - 1].focus()
        }
      } else {
        e.target.value = ''
        const newCode = [...code]
        newCode[index] = ''
        setCode(newCode)
      }
    }
  }

  // Handle verification
  const handleVerification = async () => {
    const verificationCode = code.join('')
    if (verificationCode.length !== 6) {
      return
    }

    try {
      const fullPhone = targetPhone ? (countryCode ? `${countryCode}${targetPhone}` : targetPhone) : undefined
      const payload = fullPhone ? { otp: verificationCode, phone: fullPhone } : verificationCode
      await dispatch(confirmVerifyPhone(payload)).unwrap()
    } catch (error) {
      // Clear the code inputs on error
      setCode(['', '', '', '', '', ''])
      inputsRef.current[0]?.focus()
    }
  }

  // Handle resend code
  const handleResendCode = async () => {
    setIsResending(true)
    try {
      const fullPhone = targetPhone ? (countryCode ? `${countryCode}${targetPhone}` : targetPhone) : undefined
      await dispatch(sendVerifyPhone(fullPhone)).unwrap()
  setTimeLeft(600) // Reset timer (10 minutes)
      setCode(['', '', '', '', '', '']) // Clear inputs
      inputsRef.current[0]?.focus()
    } catch (error) {
      // Error is handled by Redux
    } finally {
      setIsResending(false)
    }
  }

  // Handle send initial code
  const handleSendCode = async () => {
    try {
      const fullPhone = targetPhone ? (countryCode ? `${countryCode}${targetPhone}` : targetPhone) : undefined
      await dispatch(sendVerifyPhone(fullPhone)).unwrap()
  setTimeLeft(600) // Start timer (10 minutes)
    } catch (error) {
      // Error is handled by Redux
    }
  }

  // Handle cancel
  const handleCancel = () => {
    dispatch(resetPhoneVerification())
    onCancel?.()
  }

  // Manual verification trigger
  const handleManualVerify = e => {
    e.preventDefault()
    handleVerification()
  }

  // Prevent background scrolling and attach Escape key listener while modal is open
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') handleCancel()
    }

    // Lock body scroll
    const originalOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    document.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = originalOverflow
      document.removeEventListener('keydown', onKey)
    }
  }, [])

  return (
    // Backdrop
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      onKeyDown={(e) => {
        if (e.key === 'Escape') handleCancel()
      }}
    >
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={handleCancel}
        aria-hidden="true"
      />

      <div className="relative z-10 w-full max-w-md px-4">
        <Card className="flex p-8 justify-center items-center w-full">
          <div className="w-full">
            <div className="flex justify-between items-start mb-4">
              <div>
                <Typography variant="h3" className="py-2">
                  {isNewPhoneAdd ? 'Add & Verify Phone Number' : 'Verify Phone Number'}
                </Typography>
                <Typography variant="body" className="text-gray-600">
                  {!phoneVerification.otpSent
                    ? isNewPhoneAdd
                      ? `Click 'Send Code' to add and verify ${countryCode}${targetPhone}`
                      : "Click 'Send Code' to receive a verification code via SMS"
                    : `Enter the 6-digit code sent to ${isNewPhoneAdd ? `${countryCode}${targetPhone}` : 'your phone'}`
                  }
                </Typography>
              </div>

              <button
                aria-label="Close verification modal"
                onClick={handleCancel}
                className="text-gray-500 hover:text-gray-700 ml-4"
              >
                ×
              </button>
            </div>

            {/* Error Display */}
            {phoneVerification.error && (
              <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                <Typography variant="body">{phoneVerification.error}</Typography>
              </div>
            )}

            {/* Success Display */}
            {phoneVerification.verified && (
              <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
                <Typography variant="body">Phone number verified successfully!</Typography>
              </div>
            )}

            {!phoneVerification.otpSent ? (
              // Send Code Phase
              <div className="space-y-4">
                <Button
                  onClick={handleSendCode}
                  disabled={phoneVerification.loading}
                  className="w-full"
                >
                  {phoneVerification.loading ? 'Sending...' : 'Send Verification Code'}
                </Button>
                <Button
                  variant="outline"
                  onClick={handleCancel}
                  className="w-full"
                >
                  Cancel
                </Button>
              </div>
            ) : (
              // Verification Phase
              <form onSubmit={handleManualVerify}>
                {/* Code Input Fields */}
                <div className="flex gap-3 justify-center items-center mb-6 flex-nowrap">
                  {code.map((digit, index) => (
                    <input
                      key={index}
                      ref={el => (inputsRef.current[index] = el)}
                      type="text"
                      inputMode="numeric"
                      pattern="\\d*"
                      maxLength={1}
                      value={digit}
                      onChange={e => handleChange(index, e.target.value)}
                      onKeyDown={e => handleKeyDown(index, e)}
                      disabled={phoneVerification.loading}
                      aria-label={`Verification digit ${index + 1}`}
                      className={`w-12 h-12 text-center border-2 border-gray-300 rounded-lg focus:border-teal-500 focus:ring-0 appearance-none p-0
                                [&::-webkit-inner-spin-button]:hidden [&::-webkit-outer-spin-button]:hidden
                                ${phoneVerification.loading ? 'opacity-50 cursor-not-allowed' : ''}
                                ${phoneVerification.error ? 'border-red-500' : ''}
                                ${phoneVerification.verified ? 'border-green-500' : ''}`}
                    />
                  ))}
                </div>

                {/* Timer and Resend */}
                <div className="flex justify-between items-center mb-6">
                  <Typography variant="body" className="text-gray-600">
                    {timeLeft > 0 ? `Expires in: ${formatTime(timeLeft)}` : 'Code expired'}
                  </Typography>
                  <Button
                    type="button"
                    size="small"
                    variant="link"
                    className="shadow-none border-collapse text-teal-600"
                    onClick={handleResendCode}
                    disabled={isResending || timeLeft > 0}
                  >
                    {isResending ? 'Sending...' : 'Resend'}
                  </Button>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-4">
                  <Button
                    type="submit"
                    className="flex-1"
                    disabled={phoneVerification.loading || code.some(digit => digit === '')}
                  >
                    {phoneVerification.loading ? 'Verifying...' : 'Verify'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCancel}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            )}
          </div>
        </Card>
      </div>
    </div>
  )
}

export default AdminPhoneVerification